import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/** Aire mínimo contra el borde de la ventana al recolocar el menú. */
const MARGEN = 8;

/**
 * Menú contextual de Drive: uno solo para archivos, carpetas y fondo de la lista.
 *
 * Va en un portal a <body> a propósito: dentro de la lista lo recortaría el
 * `overflow` del ScrollArea, y con `position: fixed` dentro de un contenedor
 * transformado el navegador lo ancla al contenedor, no a la ventana.
 *
 * `items`: [{ id, label, Icon, onSelect, danger, disabled, color, destacado } | { sep: true }]
 * `color` pinta el ícono con el color del producto (el menú «Nuevo» distingue así documento de
 * hoja y de presentación); `destacado` resalta la opción principal.
 */
export default function MenuContextual({ x, y, items, onClose, testId = 'drive-menu', ancho = false }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null); // null = todavía sin medir
  const opciones = items.filter(Boolean);

  // Se mide DESPUÉS de pintar porque el alto depende de cuántas acciones
  // aplican a este archivo; hasta entonces el menú va oculto para que no se
  // vea saltar de sitio.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const caja = el.getBoundingClientRect();
    const izquierda = x + caja.width > window.innerWidth - MARGEN
      ? Math.max(MARGEN, x - caja.width)
      : x;
    const arriba = y + caja.height > window.innerHeight - MARGEN
      ? Math.max(MARGEN, y - caja.height)
      : y;
    setPos({ left: izquierda, top: arriba });
  }, [x, y, opciones.length]);

  /** Mueve el foco por las opciones activas: 1/-1 relativo, 'primero'/'ultimo'. */
  const enfocar = useCallback((dir) => {
    const el = ref.current;
    if (!el) return;
    const botones = Array.from(el.querySelectorAll('[role="menuitem"]:not([disabled])'));
    if (!botones.length) return;
    const actual = botones.indexOf(document.activeElement);
    let i;
    if (dir === 'primero') i = 0;
    else if (dir === 'ultimo') i = botones.length - 1;
    else if (actual === -1) i = dir === 1 ? 0 : botones.length - 1;
    else i = (actual + dir + botones.length) % botones.length;
    botones[i]?.focus();
  }, []);

  useEffect(() => {
    const fuera = (e) => { if (!ref.current?.contains(e.target)) onClose(); };
    const tecla = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); enfocar(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); enfocar(-1); }
      else if (e.key === 'Home') { e.preventDefault(); enfocar('primero'); }
      else if (e.key === 'End') { e.preventDefault(); enfocar('ultimo'); }
      else if (e.key === 'Tab') { e.preventDefault(); enfocar(e.shiftKey ? -1 : 1); }
    };
    document.addEventListener('mousedown', fuera, true);
    document.addEventListener('keydown', tecla, true);
    // En captura: el scroll de la lista lo hace el viewport del ScrollArea y NO
    // burbujea hasta window; sin capturarlo el menú se quedaría flotando lejos
    // de la fila que lo abrió.
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', fuera, true);
      document.removeEventListener('keydown', tecla, true);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose, enfocar]);

  // El contenedor toma el foco al abrir para que Escape y las flechas funcionen
  // aunque el clic derecho no haya dejado el foco en ningún botón.
  useEffect(() => { ref.current?.focus({ preventScroll: true }); }, []);

  return createPortal(
    <div
      ref={ref}
      role="menu"
      tabIndex={-1}
      aria-orientation="vertical"
      data-testid={testId}
      className={`gdrive-menu${ancho ? ' is-ancho' : ''}`}
      style={{
        left: pos?.left ?? x,
        top: pos?.top ?? y,
        visibility: pos ? 'visible' : 'hidden',
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {opciones.map((it, i) => (it.sep ? (
        // eslint-disable-next-line react/no-array-index-key
        <div key={`sep-${i}`} className="gdrive-menu-sep" role="separator" />
      ) : (
        /* La acción corre EN el mismo gesto, no en un setTimeout: "abrir en una
           pestaña nueva" es un window.open y el navegador lo bloquearía si
           perdiera el clic del usuario. */
        <button
          key={it.id}
          type="button"
          role="menuitem"
          disabled={it.disabled}
          data-testid={`drive-menu-${it.id}`}
          className={it.danger ? 'is-danger' : ''}
          onClick={() => { onClose(); it.onSelect?.(); }}
        >
          {/* `size` es para los íconos que no son de lucide (AuroraLogo, que
              dibuja un <img> y no entiende clases de Tailwind). */}
          {it.Icon ? <it.Icon className="h-4 w-4" size={16} style={it.color ? { color: it.color } : undefined} /> : null}
          <span className={`truncate${it.destacado ? ' font-semibold' : ''}`}>{it.label}</span>
        </button>
      )))}
    </div>,
    document.body,
  );
}
